import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { $api, fetchClient } from "../generated/client";
import type { ShopListItemDto } from "../generated/schema";

const BOOKMARKS_PATH = "/super-admin/shop-bookmarks";
const BOOKMARK_PATH = "/super-admin/shop-bookmarks/{shopId}";

const bookmarksOptions = () => $api.queryOptions("get", BOOKMARKS_PATH);

/**
 * The shops a super admin pinned to the "Dashboard" group of their shop
 * switcher (BAB-3035): off the shop teams, they have no shop of their own
 * there. Disabled for anyone else: the route is behind SuperAdminGuard.
 */
export function useSuperAdminShopBookmarks(isEnabled: boolean) {
  return useQuery({
    ...bookmarksOptions(),
    enabled: isEnabled,
    staleTime: 5 * 60_000,
  });
}

interface ToggleShopBookmark {
  shop: Pick<ShopListItemDto, "id" | "name"> & Partial<ShopListItemDto>;
  isBookmarked: boolean;
}

/** Pins or unpins a shop, shown at once and rolled back if the server refuses. */
export function useToggleSuperAdminShopBookmark() {
  const queryClient = useQueryClient();
  const { queryKey } = bookmarksOptions();

  return useMutation({
    mutationFn: async ({ shop, isBookmarked }: ToggleShopBookmark) => {
      const init = { params: { path: { shopId: shop.id } } };
      const { error } = isBookmarked
        ? await fetchClient.PUT(BOOKMARK_PATH, init)
        : await fetchClient.DELETE(BOOKMARK_PATH, init);
      if (error) throw new Error("Shop bookmark not saved");
    },
    onMutate: async ({ shop, isBookmarked }: ToggleShopBookmark) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ShopListItemDto[]>(queryKey);
      const others = (previous ?? []).filter(({ id }) => id !== shop.id);
      queryClient.setQueryData<ShopListItemDto[]>(
        queryKey,
        isBookmarked
          ? [...others, { plan: {}, ...shop }].sort((a, b) =>
              a.name.localeCompare(b.name),
            )
          : others,
      );
      return { previous };
    },
    onError: (_error, _variables, context) =>
      queryClient.setQueryData(queryKey, context?.previous),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
